import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "default";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary: "bg-accent text-bg hover:opacity-90",
  secondary: "bg-bg-elevated text-text border border-border hover:bg-bg-subtle",
  ghost: "text-text hover:bg-bg-subtle",
  danger: "bg-danger text-bg hover:opacity-90 [data-variant=danger]",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-7 px-2 text-xs gap-1",
  default: "h-9 px-3 text-sm gap-2",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "default", loading, disabled, iconLeft, iconRight, children, className, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      data-variant={variant}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded font-medium",
        "transition-opacity duration-150",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
});
